import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog, DialogActions, DialogContent, DialogTitle, Button, Chip, Badge,
  Tooltip, Divider
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import { toast } from 'react-hot-toast';
import { getApiUrl } from '../config';

const ContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchMessages();
    fetchUnreadCount();
  }, []);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(getApiUrl('/api/contact-messages'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(res.data);
    } catch (err) {
      console.error('Error fetching messages:', err);
      if (err.response?.status === 401) {
        toast.error('توکن منقضی شده است. لطفاً دوباره وارد شوید.');
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        toast.error('خطا در دریافت پیام‌ها');
      }
    }
    setLoading(false);
  };

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(getApiUrl('/api/contact-messages/unread/count'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(res.data.count);
    } catch (err) {
      console.error('Error fetching unread count:', err);
    }
  };

  const handleViewMessage = async (message) => {
    setSelectedMessage(message);
    setOpenDialog(true);
    
    // اگر پیام نخوانده است، آن را علامت‌گذاری کن
    if (!message.is_read) {
      try {
        const token = localStorage.getItem('token');
        await axios.put(getApiUrl(`/api/contact-messages/${message.id}/read`), {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // بروزرسانی وضعیت پیام
        setMessages(prev => prev.map(msg => 
          msg.id === message.id ? { ...msg, is_read: true } : msg
        ));
        
        // بروزرسانی تعداد پیام‌های نخوانده
        setUnreadCount(prev => Math.max(0, prev - 1));
        
        toast.success('پیام به عنوان خوانده شده علامت‌گذاری شد');
      } catch (err) {
        console.error('Error marking message as read:', err);
      }
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm('آیا مطمئن هستید که می‌خواهید این پیام را حذف کنید؟')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(getApiUrl(`/api/contact-messages/${id}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessages(prev => prev.filter(msg => msg.id !== id));
      toast.success('پیام با موفقیت حذف شد');
      
      // اگر پیام حذف شده نخوانده بود، تعداد را کاهش بده
      const deletedMessage = messages.find(msg => msg.id === id);
      if (deletedMessage && !deletedMessage.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Error deleting message:', err);
      toast.error('خطا در حذف پیام');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box sx={{ p: 3, fontFamily: 'Vazir' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <EmailIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h5" sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>
          مدیریت پیام‌های تماس
        </Typography>
        {unreadCount > 0 && (
          <Badge badgeContent={unreadCount} color="error">
            <Chip 
              label={`${unreadCount} پیام نخوانده`} 
              color="error" 
              variant="outlined"
              size="small"
            />
          </Badge>
        )}
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ fontFamily: 'Vazir' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.100' }}>
                <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>نام</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>ایمیل</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>شماره تماس</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>پیام</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>وضعیت</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>تاریخ</TableCell>
                <TableCell sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.map((message) => (
                <TableRow 
                  key={message.id} 
                  sx={{ 
                    backgroundColor: !message.is_read ? 'rgba(255, 193, 7, 0.1)' : 'inherit',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                  }}
                >
                  <TableCell sx={{ fontFamily: 'Vazir' }}>{message.firstname}</TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir' }}>
                    <a href={`mailto:${message.email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                      {message.email}
                    </a>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir' }}>
                    {message.phone ? (
                      <a href={`tel:${message.phone}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                        {message.phone}
                      </a>
                    ) : (
                      <span style={{ color: 'grey' }}>-</span>
                    )}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir', maxWidth: 200 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis', 
                        whiteSpace: 'nowrap',
                        fontFamily: 'Vazir'
                      }}
                    >
                      {message.message}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={message.is_read ? 'خوانده شده' : 'نخوانده'} 
                      color={message.is_read ? 'success' : 'warning'}
                      size="small"
                      sx={{ fontFamily: 'Vazir' }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Vazir', fontSize: '0.875rem' }}>
                    {formatDate(message.created_at)}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="مشاهده پیام" sx={{ fontFamily: 'Vazir' }}>
                        <IconButton 
                          onClick={() => handleViewMessage(message)}
                          color="primary"
                          size="small"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="حذف پیام" sx={{ fontFamily: 'Vazir' }}>
                        <IconButton 
                          onClick={() => handleDeleteMessage(message.id)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog برای نمایش جزئیات پیام */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: 'Vazir', fontWeight: 'bold' }}>
          جزئیات پیام
        </DialogTitle>
        <DialogContent>
          {selectedMessage && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontFamily: 'Vazir', fontWeight: 'bold', color: 'grey.600' }}>
                  نام:
                </Typography>
                <Typography sx={{ fontFamily: 'Vazir' }}>
                  {selectedMessage.firstname}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontFamily: 'Vazir', fontWeight: 'bold', color: 'grey.600' }}>
                  ایمیل:
                </Typography>
                <Typography sx={{ fontFamily: 'Vazir' }}>
                  <a href={`mailto:${selectedMessage.email}`} style={{ color: 'inherit' }}>
                    {selectedMessage.email}
                  </a>
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontFamily: 'Vazir', fontWeight: 'bold', color: 'grey.600' }}>
                  شماره تماس:
                </Typography>
                <Typography sx={{ fontFamily: 'Vazir' }}>
                  {selectedMessage.phone ? (
                    <a href={`tel:${selectedMessage.phone}`} style={{ color: 'inherit' }}>
                      {selectedMessage.phone}
                    </a>
                  ) : (
                    <span style={{ color: 'grey' }}>ارائه نشده</span>
                  )}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontFamily: 'Vazir', fontWeight: 'bold', color: 'grey.600' }}>
                  پیام:
                </Typography>
                <Typography sx={{ fontFamily: 'Vazir', mt: 1, lineHeight: 1.6 }}>
                  {selectedMessage.message}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontFamily: 'Vazir', fontWeight: 'bold', color: 'grey.600' }}>
                  تاریخ ارسال:
                </Typography>
                <Typography sx={{ fontFamily: 'Vazir' }}>
                  {formatDate(selectedMessage.created_at)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenDialog(false)}
            sx={{ fontFamily: 'Vazir' }}
          >
            بستن
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ContactMessages; 